import { Box } from '@mui/material';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import XIcon from '@/public/icons/record/x.svg';
import { StyledSVGButton } from '@/styles/GlobalStyles';
import useScrollLock from '@/hooks/useScrollLock';
import {
  DrawerContent,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  HeaderTitleLayout,
  StyledDrawer,
  StyledSwipeableDrawer,
  StyledTitle,
} from '@/components/@IntranetLibrary/MobileBottomSheet/styles';

// 모바일 표준 바텀시트. (구 pages/m/CustomMobileDrawer)
// PC 전역 시트는 @IntranetLibrary/PcBottomSheet + useBottomSheet() 를 쓴다.

export type MobileBottomSheetProps = {
  children: React.ReactNode;

  /** 선언형 제어. 넘기면 controlled, 생략하면 ref 제어(레거시). */
  open?: boolean;
  /** 닫힘 콜백. controlled 모드에선 여기서 open 을 false 로 바꿔야 닫힌다. */
  onClose?: () => void;

  title?: React.ReactNode;
  leftIcon?: React.ReactNode;
  /** 하단 고정 영역. safe-area-inset-bottom 자동 적용. */
  footer?: React.ReactNode;
  /** 상단 그랩 핸들바 노출. title 이 있으면 무시된다. */
  showHandle?: boolean;

  height?: string;
  /** Optional max-height constraint for content-based height with cap */
  maxHeight?: string;
  /**
   * height/maxHeight 적용 대상.
   * - 'content'(기본): 본문(DrawerContent)에만 적용 — 시트 총 높이 = 헤더/핸들 + height.
   * - 'sheet': 시트(Paper) 전체에 적용 — 헤더/핸들 포함 총 높이 = height. 본문은 남는 공간을 채운다.
   */
  heightMode?: 'content' | 'sheet';

  /**
   * 열고 닫을 때 RN WebView 로 `BAR_STATUS` 를 보내 앱 하단 바를 숨길지 여부.
   * 백드롭(딤)과는 무관하다 — 딤은 MUI 가 항상 그린다. (구 `dimmed` 를 실제 동작에 맞춰 개명)
   *
   * 기본 false. **앱 하단 바를 정말 가려야 하는 전체화면급 시트에서만 켠다.**
   * 켜는 쪽은 시트가 닫힐 때 반드시 unmount 가 아니라 open=false 를 거치게 하거나,
   * 아래 언마운트 보정에 의존해도 되는지 확인할 것.
   */
  notifyAppBar?: boolean;
  /** Optional z-index override for layering above other overlays */
  zIndex?: number;
  /** Sheet color theme. 'dark' renders a dark paper/header (e.g. AI Viewer). */
  variant?: 'light' | 'dark';

  /** false 면 스와이프로 닫히지 않는 MUI Drawer 로 렌더 (전자결재 계열). */
  swipeable?: boolean;
  keepMounted?: boolean;
  transitionDuration?: number;
};

export type MobileBottomSheetRef = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const MobileBottomSheet = forwardRef<
  MobileBottomSheetRef,
  MobileBottomSheetProps
>(
  (
    {
      children,
      open: openProp,
      onClose,
      title,
      leftIcon,
      footer,
      showHandle = true,
      height = '100vh',
      maxHeight,
      heightMode = 'content',
      notifyAppBar = false,
      zIndex,
      variant = 'light',
      swipeable = true,
      keepMounted = true,
      transitionDuration,
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = openProp !== undefined;
    const isOpen = isControlled ? openProp : internalOpen;

    // MUI 기본 락은 body 만 잠그는데 포탈은 html 이 실제 스크롤러라
    // 배경이 그대로 밀린다. html 을 잠그는 공용 훅으로 보강. (중첩 오픈은 카운트 관리)
    useScrollLock(isOpen);

    const isDark = variant === 'dark';
    const isSheetHeight = heightMode === 'sheet';

    useImperativeHandle(ref, () => ({
      open: () => {
        if (isControlled) {
          warnControlledRefUse('open');
          return;
        }
        setInternalOpen(true);
      },
      close: () => {
        if (isControlled) {
          warnControlledRefUse('close');
          return;
        }
        setInternalOpen(false);
      },
      toggle: () => {
        if (isControlled) {
          warnControlledRefUse('toggle');
          return;
        }
        setInternalOpen((prev) => !prev);
      },
    }));

    const handleOpen = () => {
      if (!isControlled) setInternalOpen(true);
    };

    const handleClose = () => {
      if (!isControlled) setInternalOpen(false);
      onClose?.();
    };

    // 앱 하단 바에 '열림'을 알린 채로 사라지면 바가 숨은 채 남는다.
    // 호출부가 open=false 를 거치지 않고 언마운트해도 복구되도록 보정한다.
    const notifiedOpenRef = useRef(false);

    useEffect(() => {
      if (!notifyAppBar) return;
      postBarStatus(isOpen);
      notifiedOpenRef.current = isOpen;
    }, [isOpen, notifyAppBar]);

    useEffect(
      () => () => {
        if (notifiedOpenRef.current) postBarStatus(false);
      },
      [],
    );

    const sx = {
      width: '100%',
      ...(zIndex !== undefined && { zIndex }),
      '& .MuiDrawer-paper': {
        // 'sheet' 모드에서만 height/maxHeight 를 시트(Paper) 전체에 적용한다.
        // 'content' 모드(기본)는 아래 DrawerContent 에만 적용.
        ...(isSheetHeight && {
          height,
          ...(maxHeight && { maxHeight }),
        }),
        ...(isDark && {
          backgroundColor: '#2B2D2F',
          color: '#FFFFFF',
        }),
      },
    };

    const inner = (
      <>
        {title ? (
          <HeaderTitleLayout
            style={isDark ? { borderBottomColor: '#393D41' } : undefined}
          >
            {leftIcon && <div style={{ marginRight: 'auto' }}>{leftIcon}</div>}
            <StyledTitle>{title}</StyledTitle>
            <StyledSVGButton
              onClick={handleClose}
              style={{ zIndex: '100', marginLeft: 'auto' }}
            >
              {/* x.svg 는 path 에 stroke="black" 가 박혀 있어 svg prop 으로는
                  색이 안 바뀐다. CSS(우선순위 상위)로 path stroke 를 덮어쓴다. */}
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  '& path': { stroke: isDark ? '#FFFFFF' : 'black' },
                }}
              >
                <XIcon width={14} height={14} />
              </Box>
            </StyledSVGButton>
          </HeaderTitleLayout>
        ) : (
          <>
            {showHandle && (
              <DrawerHeader>
                <DrawerHandle />
              </DrawerHeader>
            )}
          </>
        )}
        <DrawerContent
          style={
            isSheetHeight
              ? { flex: 1, minHeight: 0 }
              : { height, ...(maxHeight && { maxHeight }) }
          }
        >
          {children}
        </DrawerContent>
        {footer && <DrawerFooter>{footer}</DrawerFooter>}
      </>
    );

    if (!swipeable) {
      return (
        <StyledDrawer
          anchor="bottom"
          open={isOpen}
          onClose={handleClose}
          ModalProps={{ keepMounted }}
          transitionDuration={transitionDuration}
          sx={sx}
        >
          {inner}
        </StyledDrawer>
      );
    }

    return (
      <StyledSwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        swipeAreaWidth={56}
        disableSwipeToOpen={true}
        ModalProps={{ keepMounted }}
        transitionDuration={transitionDuration}
        sx={sx}
      >
        {inner}
      </StyledSwipeableDrawer>
    );
  },
);

MobileBottomSheet.displayName = 'MobileBottomSheet';

export default MobileBottomSheet;

/** RN 앱 하단 바 표시 상태 전달. 웹(앱 밖)에서는 no-op. */
const postBarStatus = (open: boolean) => {
  window?.ReactNativeWebView?.postMessage(
    JSON.stringify({ actionType: 'BAR_STATUS', data: open }),
  );
};

const warnControlledRefUse = (method: string) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[MobileBottomSheet] open prop 이 있는 controlled 모드에선 ref.${method}() 가 무시됩니다. open prop 으로 제어하세요.`,
    );
  }
};
